import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import {
  Plus,
  Search,
  FolderOpen,
  Calendar,
  MapPin,
  User,
  Filter,
  Target,
  Unlink,
} from "lucide-react";
import { ToolIcon } from "@/components/tool-icon";
import { useProjectContext } from "@/lib/project-context";
import type { Project } from "@shared/schema";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Projects() {
  const [search, setSearch] = useState("");
  const [filterTool, setFilterTool] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const { currentProject, setCurrentProject, clearProject } = useProjectContext();

  const { data: projects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  const filtered = projects?.filter((p) => {
    const matchesSearch =
      !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.pipelineName.toLowerCase().includes(search.toLowerCase()) ||
      (p.clientName && p.clientName.toLowerCase().includes(search.toLowerCase()));
    const matchesTool = filterTool === "all" || p.toolType === filterTool;
    const matchesStatus = filterStatus === "all" || p.status === filterStatus;
    return matchesSearch && matchesTool && matchesStatus;
  }) ?? [];

  const sorted = [...filtered].sort((a, b) => {
    const aFocused = currentProject?.id === a.id ? 1 : 0;
    const bFocused = currentProject?.id === b.id ? 1 : 0;
    return bFocused - aFocused;
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-4 gap-3">
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="input-search-projects"
            />
          </div>
          <Select value={filterTool} onValueChange={setFilterTool}>
            <SelectTrigger className="w-[150px]" data-testid="select-filter-tool">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Tool" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tools</SelectItem>
              <SelectItem value="dragonball">DragonBall</SelectItem>
              <SelectItem value="sparrow">Sparrow</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]" data-testid="select-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="planning">Planning</SelectItem>
              <SelectItem value="field">Field</SelectItem>
              <SelectItem value="analysis">Check Data</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Link href="/projects/new">
          <Button size="sm" data-testid="button-new-project">
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        </Link>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-md" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No projects found</h3>
          {!search && (
            <Link href="/projects/new">
              <Button data-testid="button-create-project-empty">
                <Plus className="mr-2 h-4 w-4" />
                Create Project
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {sorted.map((project) => {
            const isFocused = currentProject?.id === project.id;
            return (
              <div key={project.id} className="relative">
                <Link href={`/projects/${project.id}`}>
                  <Card className={`hover-elevate cursor-pointer h-full ${isFocused ? "border-2 border-teal-500 dark:border-teal-400" : ""}`} data-testid={`card-project-${project.id}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
                            <ToolIcon type={project.toolType as "dragonball" | "sparrow"} className="h-7 w-7" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold truncate" data-testid={`text-project-${project.id}`}>
                              {project.name}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {project.toolType === "dragonball" ? "DragonBall" : "Sparrow"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isFocused && (
                            <Badge variant="outline" className="text-[10px] border-teal-500 text-teal-600 dark:text-teal-400 dark:border-teal-400" data-testid="badge-focused-project">
                              Focused
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-[10px] ${project.isInternal === false ? "border-orange-300 text-orange-600 dark:text-orange-400 dark:border-orange-500" : "border-blue-300 text-blue-600 dark:text-blue-400 dark:border-blue-500"}`} data-testid={`badge-type-${project.id}`}>
                            {project.isInternal === false ? "External" : "Internal"}
                          </Badge>
                          <ProjectStatusBadge status={project.status} />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{project.pipelineName}</span>
                        </div>
                        {project.location && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{project.location}</span>
                          </div>
                        )}
                        {project.clientName && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <User className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{project.clientName}</span>
                          </div>
                        )}
                        {project.createdAt && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5 shrink-0" />
                            <span>{new Date(project.createdAt).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-center gap-2 pt-1 border-t">
                        {isFocused ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs bg-orange-500 text-white border-orange-500 hover:bg-orange-600 hover:text-white"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearProject(); }}
                            data-testid="button-unlink-project"
                          >
                            <Unlink className="h-3 w-3 mr-1" />
                            Unlink
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs bg-[#1d4ed8] text-white border-[#1d4ed8] hover:bg-[#1e40af] hover:text-white"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentProject(project); }}
                            data-testid={`button-focus-project-${project.id}`}
                          >
                            <Target className="h-3 w-3 mr-1" />
                            Focus
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

function ProjectStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    planning: { label: "Planning", variant: "secondary" },
    field: { label: "In Field", variant: "default" },
    analysis: { label: "Check Data", variant: "outline" },
    complete: { label: "Complete", variant: "secondary" },
  };
  const { label, variant } = config[status] || { label: status, variant: "outline" as const };
  return <Badge variant={variant} className="text-xs shrink-0">{label}</Badge>;
}
